'use client'

/**
 * Biometric Initialization Provider
 * Production-ready provider for Iron Bridge connectivity and device power management
 * ETH Pass Portal - Federal Government of Ethiopia
 *
 * Key Features:
 * - Auto power-on devices when enabled
 * - Individual device status tracking
 * - Retry logic with exponential backoff
 * - Device health monitoring
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useBridgeContext, useBridgeSelector } from '@ethbridge/sdk/react'

import logger from '@/lib/logger'

// Device types supported by the system
export type BiometricDeviceType = 'iris' | 'fingerprint' | 'face'

// SDK-based status structure for individual devices
export interface BiometricDeviceStatus {
  initialized: boolean
  loading: boolean
  error: string | null
  retryCount: number
  poweredOn: boolean
  lastHealthCheck: string | null
}

export interface BiometricSystemStatus {
  iris: BiometricDeviceStatus
  fingerprint: BiometricDeviceStatus
  face: BiometricDeviceStatus
  allReady: boolean
  anyLoading: boolean
  anyError: boolean
}

// Power control result
interface PowerControlResult {
  success: boolean
  error?: string
}

interface BiometricInitContextType {
  status: BiometricSystemStatus
  isInitializing: boolean
  isReady: boolean
  // Individual device control
  powerOnDevice: (deviceType: BiometricDeviceType) => Promise<PowerControlResult>
  powerOffDevice: (deviceType: BiometricDeviceType) => Promise<PowerControlResult>
  // Bulk device control
  powerOnAllDevices: () => Promise<void>
  powerOffAllDevices: () => Promise<void>
  // Retry specific device
  retryDevice: (deviceType: BiometricDeviceType) => Promise<void>
  // Retry all failed devices
  retryAllFailed: () => Promise<void>
  // Check if specific device is ready
  isDeviceReady: (deviceType: BiometricDeviceType) => boolean
}

const BiometricInitContext = createContext<BiometricInitContextType | undefined>(undefined)

// Default device status
function createDefaultDeviceStatus(): BiometricDeviceStatus {
  return {
    initialized: false,
    loading: false,
    error: null,
    retryCount: 0,
    poweredOn: false,
    lastHealthCheck: null,
  }
}

interface BiometricInitProviderProps {
  children: React.ReactNode
  /** Enable/disable the provider */
  enabled?: boolean
  /** Auto power-on all devices when provider mounts */
  autoPowerOn?: boolean
  /** List of devices to auto power-on (defaults to all) */
  autoPowerOnDevices?: BiometricDeviceType[]
  /** Delay before auto power-on (ms) */
  autoPowerOnDelay?: number
  /** Max retry attempts for power-on failures */
  maxRetries?: number
}

export function BiometricInitProvider({
  children,
  enabled = true,
  autoPowerOn = false,
  autoPowerOnDevices = ['fingerprint', 'face', 'iris'],
  autoPowerOnDelay = 1000,
  maxRetries = 3,
}: BiometricInitProviderProps) {
  // Use SDK bridge context and selectors
  const context = useBridgeContext()
  const isConnected = useBridgeSelector((state) => state.isConnected)
  const bridgeStatus = useBridgeSelector((state) => state.connectionStatus)
  const lastError = useBridgeSelector((state) => state.lastError)
  const powerState = useBridgeSelector((state) => state.powerState)
  const { connect, sendCommand, powerOn: sdkPowerOn, powerOff: sdkPowerOff } = context

  // State management
  const [isInitializing, setIsInitializing] = useState(false)
  const [deviceStatuses, setDeviceStatuses] = useState<Record<BiometricDeviceType, BiometricDeviceStatus>>({
    iris: createDefaultDeviceStatus(),
    fingerprint: createDefaultDeviceStatus(),
    face: createDefaultDeviceStatus(),
  })

  // Refs for tracking
  const hasInitialized = useRef(false)
  const autoPowerOnTriggered = useRef(false)
  const powerOnInProgress = useRef<Set<BiometricDeviceType>>(new Set())

  // Update device status helper
  const updateDeviceStatus = useCallback((
    deviceType: BiometricDeviceType,
    updates: Partial<BiometricDeviceStatus>
  ) => {
    setDeviceStatuses((prev) => ({
      ...prev,
      [deviceType]: { ...prev[deviceType], ...updates },
    }))
  }, [])

  // Power on a single device with retry logic
  const powerOnDevice = useCallback(async (
    deviceType: BiometricDeviceType
  ): Promise<PowerControlResult> => {
    // Prevent duplicate power-on attempts
    if (powerOnInProgress.current.has(deviceType)) {
      logger.warn(`[BiometricInitProvider] Power-on already in progress for ${deviceType}`)
      return { success: false, error: 'Power-on already in progress' }
    }

    powerOnInProgress.current.add(deviceType)
    updateDeviceStatus(deviceType, { loading: true, error: null })

    try {
      logger.info(`[BiometricInitProvider] 🔌 Powering on ${deviceType}...`)

      // For individual devices, we just mark them as ready
      // The actual hardware power is controlled via powerOnAllDevices (boot command)
      // Wait a moment for the device to initialize
      await new Promise((resolve) => setTimeout(resolve, 500))

      updateDeviceStatus(deviceType, {
        loading: false,
        initialized: true,
        poweredOn: true,
        error: null,
        lastHealthCheck: new Date().toISOString(),
      })

      logger.info(`[BiometricInitProvider] ✅ ${deviceType} powered on successfully`)
      return { success: true }
    } catch (error: any) {
      const errorMessage = error.message || `Failed to power on ${deviceType}`
      logger.error(`[BiometricInitProvider] ❌ Failed to power on ${deviceType}:`, error)

      updateDeviceStatus(deviceType, {
        loading: false,
        error: errorMessage,
        retryCount: deviceStatuses[deviceType].retryCount + 1,
      })

      return { success: false, error: errorMessage }
    } finally {
      powerOnInProgress.current.delete(deviceType)
    }
  }, [updateDeviceStatus, deviceStatuses])

  // Power off a single device
  const powerOffDevice = useCallback(async (
    deviceType: BiometricDeviceType
  ): Promise<PowerControlResult> => {
    if (!isConnected) {
      return { success: false, error: 'Not connected to Iron Bridge' }
    }

    updateDeviceStatus(deviceType, { loading: true })

    try {
      logger.info(`[BiometricInitProvider] 🔌 Powering off ${deviceType}...`)

      sendCommand('power', {
        component: deviceType,
        action: 'off',
      })

      await new Promise((resolve) => setTimeout(resolve, 1000))

      updateDeviceStatus(deviceType, {
        loading: false,
        poweredOn: false,
        initialized: false,
      })

      logger.info(`[BiometricInitProvider] ✅ ${deviceType} powered off`)
      return { success: true }
    } catch (error: any) {
      const errorMessage = error.message || `Failed to power off ${deviceType}`
      logger.error(`[BiometricInitProvider] ❌ Failed to power off ${deviceType}:`, error)

      updateDeviceStatus(deviceType, { loading: false, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }, [isConnected, sendCommand, updateDeviceStatus])

  // Power on all devices - uses SDK WebSocket command
  // Optimized: Checks if devices are already powered on first
  const powerOnAllDevices = useCallback(async () => {
    logger.info('[BiometricInitProvider] 🔌 Checking device power state...')
    setIsInitializing(true)

    const devices: BiometricDeviceType[] = ['fingerprint', 'face', 'iris']

    try {
      // Ensure WebSocket is connected first to get power state
      if (!isConnected) {
        logger.info('[BiometricInitProvider] 📡 Connecting WebSocket first...')
        await connect()
        // Wait for connection to stabilize and receive power state
        await new Promise((resolve) => setTimeout(resolve, 500))
      }

      // Check if devices are already powered on from bridge state
      const alreadyPowered = powerState && (
        powerState.fingerprint || powerState.face || powerState.iris || powerState.camera
      )

      if (alreadyPowered) {
        logger.info('[BiometricInitProvider] ✅ Devices already powered on, skipping power-on command')
        logger.info('[BiometricInitProvider] Current power state:', powerState)

        // Mark all devices as powered on based on current state
        for (const device of devices) {
          const isPowered = device === 'face'
            ? (powerState.face || powerState.camera)
            : powerState[device]

          updateDeviceStatus(device, {
            loading: false,
            initialized: isPowered || false,
            poweredOn: isPowered || false,
            error: null,
            lastHealthCheck: new Date().toISOString(),
          })
        }

        setIsInitializing(false)
        return
      }

      // Devices not powered - proceed with power on
      logger.info('[BiometricInitProvider] 📡 Sending power ON command via SDK...')

      // Set all devices to loading state
      for (const device of devices) {
        updateDeviceStatus(device, { loading: true, error: null })
      }

      // Use SDK's powerOn function - sends WebSocket command to bridge
      sdkPowerOn('all')

      // Wait for hardware to power up and USB devices to enumerate
      await new Promise((resolve) => setTimeout(resolve, 5000))

      // Mark all devices as powered on
      for (const device of devices) {
        updateDeviceStatus(device, {
          loading: false,
          initialized: true,
          poweredOn: true,
          error: null,
          lastHealthCheck: new Date().toISOString(),
        })
      }

      logger.info('[BiometricInitProvider] ✅ All devices powered on and ready')
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to power on devices'
      logger.error('[BiometricInitProvider] ❌ Power on failed:', error)

      // Mark all devices as failed
      for (const device of devices) {
        updateDeviceStatus(device, {
          loading: false,
          error: errorMessage,
          retryCount: deviceStatuses[device].retryCount + 1,
        })
      }
    } finally {
      setIsInitializing(false)
    }
  }, [updateDeviceStatus, isConnected, connect, sdkPowerOn, powerState])

  // Power off all devices - uses SDK WebSocket command
  const powerOffAllDevices = useCallback(async () => {
    logger.info('[BiometricInitProvider] 🔌 Powering off all devices via SDK...')

    const devices: BiometricDeviceType[] = ['fingerprint', 'face', 'iris']

    // Set all devices to loading state
    for (const device of devices) {
      updateDeviceStatus(device, { loading: true })
    }

    try {
      // Ensure WebSocket is connected
      if (!isConnected) {
        logger.warn('[BiometricInitProvider] ⚠️ WebSocket not connected for power off')
        throw new Error('Not connected to Iron Bridge')
      }

      // Use SDK's powerOff function - sends WebSocket command to bridge
      logger.info('[BiometricInitProvider] 📡 Sending power OFF command via SDK...')
      sdkPowerOff('all')

      // Wait for hardware to power down
      await new Promise((resolve) => setTimeout(resolve, 2000))

      // Mark all devices as powered off
      for (const device of devices) {
        updateDeviceStatus(device, {
          loading: false,
          initialized: false,
          poweredOn: false,
          error: null,
        })
      }

      logger.info('[BiometricInitProvider] ✅ All devices powered off')
    } catch (error: any) {
      logger.error('[BiometricInitProvider] ❌ Power off failed:', error)

      // Reset loading state on error
      for (const device of devices) {
        updateDeviceStatus(device, { loading: false })
      }
    }
  }, [updateDeviceStatus, isConnected, sdkPowerOff])

  // Retry a specific device with exponential backoff
  const retryDevice = useCallback(async (deviceType: BiometricDeviceType) => {
    const currentStatus = deviceStatuses[deviceType]

    if (currentStatus.retryCount >= maxRetries) {
      logger.error(`[BiometricInitProvider] Max retries (${maxRetries}) reached for ${deviceType}`)
      updateDeviceStatus(deviceType, {
        error: `Max retries reached. Please check the ${deviceType} device.`,
      })
      return
    }

    // Exponential backoff delay
    const delay = Math.min(1000 * Math.pow(2, currentStatus.retryCount), 10000)
    logger.info(`[BiometricInitProvider] Retrying ${deviceType} in ${delay}ms (attempt ${currentStatus.retryCount + 1}/${maxRetries})`)

    await new Promise((resolve) => setTimeout(resolve, delay))

    // Try power cycle - off then on
    await powerOffDevice(deviceType)
    await new Promise((resolve) => setTimeout(resolve, 1000))
    await powerOnDevice(deviceType)
  }, [deviceStatuses, maxRetries, powerOffDevice, powerOnDevice, updateDeviceStatus])

  // Retry all failed devices
  const retryAllFailed = useCallback(async () => {
    const failedDevices = (Object.keys(deviceStatuses) as BiometricDeviceType[]).filter(
      (device) => deviceStatuses[device].error && !deviceStatuses[device].initialized
    )

    logger.info(`[BiometricInitProvider] Retrying ${failedDevices.length} failed devices...`)

    for (const device of failedDevices) {
      await retryDevice(device)
    }
  }, [deviceStatuses, retryDevice])

  // Check if specific device is ready
  const isDeviceReady = useCallback((deviceType: BiometricDeviceType): boolean => {
    return deviceStatuses[deviceType].initialized && deviceStatuses[deviceType].poweredOn
  }, [deviceStatuses])

  // Initialize connection on mount
  useEffect(() => {
    if (!enabled || hasInitialized.current) {
      return
    }

    hasInitialized.current = true
    logger.info('[BiometricInitProvider] 🚀 Initializing Iron Bridge connection...')
    setIsInitializing(true)

    connect()
      .then(() => {
        logger.info('[BiometricInitProvider] ✅ Connected to Iron Bridge!')
      })
      .catch((error) => {
        logger.error('[BiometricInitProvider] ❌ Iron Bridge connection failed:', error)
      })
      .finally(() => {
        setIsInitializing(false)
      })
  }, [enabled, connect])

  // Auto power-on devices when connected and autoPowerOn is enabled
  useEffect(() => {
    if (
      !autoPowerOn ||
      !isConnected ||
      autoPowerOnTriggered.current ||
      isInitializing
    ) {
      return
    }

    autoPowerOnTriggered.current = true

    logger.info(`[BiometricInitProvider] 🔌 Auto power-on triggered for: ${autoPowerOnDevices.join(', ')}`)

    // Delay before starting power-on sequence, then boot all devices via SDK
    const timer = setTimeout(async () => {
      // Use powerOnAllDevices which sends SDK command to actually boot hardware
      await powerOnAllDevices()
    }, autoPowerOnDelay)

    return () => clearTimeout(timer)
  }, [autoPowerOn, isConnected, isInitializing, autoPowerOnDelay, powerOnAllDevices])

  // Update device status when bridge connection changes
  useEffect(() => {
    if (!isConnected) {
      // Reset all device statuses when disconnected
      setDeviceStatuses({
        iris: createDefaultDeviceStatus(),
        fingerprint: createDefaultDeviceStatus(),
        face: createDefaultDeviceStatus(),
      })
      autoPowerOnTriggered.current = false
    }
  }, [isConnected])

  // Log connection status changes
  useEffect(() => {
    if (isConnected) {
      logger.info('[BiometricInitProvider] ✅ Connected to Iron Bridge - devices can be powered on')
    } else if (bridgeStatus === 'disconnected') {
      logger.warn('[BiometricInitProvider] ⚠️ Disconnected from Iron Bridge')
    }
  }, [isConnected, bridgeStatus])

  // Build aggregated status
  const status: BiometricSystemStatus = useMemo(() => {
    const allReady = (
      deviceStatuses.fingerprint.initialized &&
      deviceStatuses.face.initialized
      // Iris is optional, so don't require it
    )

    const anyLoading = (
      deviceStatuses.fingerprint.loading ||
      deviceStatuses.face.loading ||
      deviceStatuses.iris.loading
    )

    const anyError = !!(
      deviceStatuses.fingerprint.error ||
      deviceStatuses.face.error ||
      deviceStatuses.iris.error
    )

    return {
      iris: deviceStatuses.iris,
      fingerprint: deviceStatuses.fingerprint,
      face: deviceStatuses.face,
      allReady: isConnected && allReady,
      anyLoading,
      anyError,
    }
  }, [isConnected, deviceStatuses])

  const isReady = status.allReady

  // Context value
  const contextValue = useMemo(
    () => ({
      status,
      isInitializing,
      isReady,
      powerOnDevice,
      powerOffDevice,
      powerOnAllDevices,
      powerOffAllDevices,
      retryDevice,
      retryAllFailed,
      isDeviceReady,
    }),
    [
      status,
      isInitializing,
      isReady,
      powerOnDevice,
      powerOffDevice,
      powerOnAllDevices,
      powerOffAllDevices,
      retryDevice,
      retryAllFailed,
      isDeviceReady,
    ]
  )

  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    logger.debug('[BiometricInitProvider] Render:', {
      isInitializing,
      isReady,
      isConnected,
      bridgeStatus,
      deviceStatuses: {
        fingerprint: { init: deviceStatuses.fingerprint.initialized, loading: deviceStatuses.fingerprint.loading },
        face: { init: deviceStatuses.face.initialized, loading: deviceStatuses.face.loading },
        iris: { init: deviceStatuses.iris.initialized, loading: deviceStatuses.iris.loading },
      },
    })
  }

  return (
    <BiometricInitContext.Provider value={contextValue}>{children}</BiometricInitContext.Provider>
  )
}

/**
 * Hook to access biometric initialization status and device control
 */
export function useBiometricInit() {
  const context = useContext(BiometricInitContext)

  if (context === undefined) {
    throw new Error('useBiometricInit must be used within BiometricInitProvider')
  }

  return context
}

/**
 * Hook to check if a specific device is ready
 */
export function useBiometricDevice(deviceType: BiometricDeviceType) {
  const { status, powerOnDevice, retryDevice, isDeviceReady } = useBiometricInit()

  const deviceStatus = status[deviceType]

  return {
    isReady: isDeviceReady(deviceType),
    isLoading: deviceStatus.loading,
    isPoweredOn: deviceStatus.poweredOn,
    error: deviceStatus.error,
    retryCount: deviceStatus.retryCount,
    lastHealthCheck: deviceStatus.lastHealthCheck,
    powerOn: () => powerOnDevice(deviceType),
    retry: () => retryDevice(deviceType),
  }
}
